import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, Badge, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import ShafyorForm from '../../../components/shafyor/ShafyorForm';
import {toast} from "react-toastify";

const Step2Shafyors = ({ formData, updateFormData, options, setOptions }) => {
  const [selectedShafyor, setSelectedShafyor] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingShafyor, setEditingShafyor] = useState(null);
  const [errors, setErrors] = useState({});

  // Initialize selected shafyors from form data
  useEffect(() => {
    const updateAvailableShafyors = () => {
      const currentShafyors = formData.shafyors || [];
      const currentOptions = options.shafyors || [];
      
      // Create a Set of selected shafyor IDs for faster lookup
      const selectedShafyorIds = new Set(currentShafyors.map(s => s.id));
      
      // Filter out shafyors that are already selected
      const availableShafyors = currentOptions.filter(
        shafyor => !selectedShafyorIds.has(shafyor.id)
      );
      
      // Only update if there are actual changes
      setOptions(prev => {
        const currentAvailableIds = new Set((prev.availableShafyors || []).map(s => s.id));
        const newAvailableIds = new Set(availableShafyors.map(s => s.id));
        
        // Check if the available shafyors have actually changed
        const hasChanges = 
          availableShafyors.length !== (prev.availableShafyors || []).length ||
          availableShafyors.some(s => !currentAvailableIds.has(s.id)) ||
          (prev.availableShafyors || []).some(s => !newAvailableIds.has(s.id));
        
        return hasChanges 
          ? { ...prev, availableShafyors }
          : prev;
      });
    };
    
    updateAvailableShafyors();
  }, [formData.shafyors, options.shafyors, setOptions]);

  const handleAddShafyor = React.useCallback((shafyor = null) => {
    const shafyorToAdd = shafyor || selectedShafyor;
    
    console.log('handleAddShafyor called with:', { shafyor, selectedShafyor, shafyorToAdd });
    
    if (!shafyorToAdd) {
      setErrors(prev => ({
        ...prev,
        shafyor: 'Shafyorni tanlang'
      }));
      return;
    }

    // Create a clean shafyor object with only the properties we need
    const newShafyor = {
      id: shafyorToAdd.id || shafyorToAdd.value,
      name: shafyorToAdd.name || shafyorToAdd.label || '',
      phone: shafyorToAdd.phone || shafyorToAdd.phoneNumber || '',
      prava_raqam: shafyorToAdd.prava_raqam || shafyorToAdd.driverLicense || ''
    };
    
    console.log('New shafyor object:', newShafyor);

    // Update the form data directly
    updateFormData(prev => {
      const currentShafyors = Array.isArray(prev.shafyors) ? [...prev.shafyors] : [];
      
      // Check if shafyor already exists
      const isAlreadyAdded = currentShafyors.some(s => 
        s && s.id && newShafyor.id && s.id.toString() === newShafyor.id.toString()
      );
      
      if (isAlreadyAdded) {
        toast.warning('Bu shafyor allaqachon qo\'shilgan');
        return {}; // Return empty object to prevent update
      }
      
      // Add the new shafyor to the list
      const updatedShafyors = [...currentShafyors, newShafyor];
      
      console.log('Adding shafyor, new shafyors array:', updatedShafyors);
      
      // Update available shafyors in options
      setOptions(prevOptions => ({
        ...prevOptions,
        availableShafyors: (prevOptions.availableShafyors || []).filter(
          s => s && s.id && s.id.toString() !== newShafyor.id.toString()
        )
      }));
      
      // Return only the changed properties
      return {
        shafyors: updatedShafyors
      };
    });

    // Reset selection if not coming from direct select
    if (!shafyor) {
      setSelectedShafyor(null);
    }
    
    // Clear any errors
    setErrors(prev => ({
      ...prev,
      shafyor: undefined
    }));
  }, [selectedShafyor, updateFormData]);

  const handleRemoveShafyor = React.useCallback((shafyorId) => {
    // Update form data first
    updateFormData(prev => {
      const updatedShafyors = prev.shafyors.filter(s => s.id !== shafyorId);
      return {
        ...prev,
        shafyors: updatedShafyors
      };
    });
  }, [updateFormData]);

  const handleEditShafyor = React.useCallback((shafyor) => {
    setEditingShafyor(shafyor);
    setShowAddModal(true);
  }, []);

  const handleSaveNewShafyor = React.useCallback(async (newShafyor) => {
    try {
      // In a real app, you would save to the backend here
      // For now, just add to available shafyors
      const newShafyorOption = {
        id: newShafyor.id,
        value: newShafyor.id,
        label: `${newShafyor.name} (${newShafyor.prava_raqam})`,
        ...newShafyor
      };

      // Update form data with the new shafyor
      updateFormData(prev => ({
        ...prev,
        shafyors: [
          ...(prev.shafyors || []), 
          {
            id: newShafyor.id,
            name: newShafyor.name,
            ...newShafyor
          }
        ]
      }));

      // Update options
      setOptions(prev => ({
        ...prev,
        shafyors: [...(prev.shafyors || []), newShafyorOption],
        availableShafyors: [...(prev.availableShafyors || []), newShafyorOption]
      }));

      toast.success('Shafyor muvaffaqiyatli qo\'shildi');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving shafyor:', error);
      toast.error('Shafyorni saqlashda xatolik yuz berdi');
    }
  }, [updateFormData]);

  // Debug log current form data
  console.log('Current formData.shafyors:', formData.shafyors);
  
  return (
    <div className="pw-form">
      <h5 className="mb-4">Shafyorlar</h5>
      
      <Row className="mb-4">
        <Col md={8}>
          <Form.Group controlId="shafyor">
            <Form.Label>Shafyorni tanlang <span className="pw-text-danger">*</span></Form.Label>
            <div className="d-flex">
              <div className="flex-grow-1 me-2">
                <Select
                  value={null}
                  onChange={(selected) => {
                    console.log('Select onChange called with:', selected);
                    if (selected) {
                      // Create a complete shafyor object with all necessary fields
                      const shafyorData = {
                        id: selected.value,
                        name: selected.label.split(' (')[0], // Extract name from label
                        phone: selected.phone || '',
                        prava_raqam: selected.prava_raqam || ''
                      };
                      console.log('Calling handleAddShafyor with:', shafyorData);
                      handleAddShafyor(shafyorData);
                    }
                  }}
                  options={(options.availableShafyors || []).map(s => ({
                    value: s.id,
                    label: `${s.name} (${s.prava_raqam || 'N/A'})`,
                    name: s.name,
                    phone: s.phone,
                    prava_raqam: s.prava_raqam
                  }))}
                  placeholder="Shafyorni tanlang..."
                  className={errors.shafyor ? 'is-invalid' : ''}
                  isClearable
                  noOptionsMessage={() => 'Shafyor topilmadi'}
                />
                <Form.Text className="pw-text-danger">{errors.shafyor}</Form.Text>
              </div>
            </div>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button 
            variant="outline-primary" 
            onClick={() => {
              setEditingShafyor(null);
              setShowAddModal(true);
            }}
          >
            <FaPlus className="me-1" /> Yangi shafyor qo'shish
          </Button>
        </Col>
      </Row>
      
      {/* Debug info - can be removed later */}
      <div style={{ display: 'none' }}>
        Form Data: {JSON.stringify(formData.shafyors, null, 2)}
      </div>
      
      {formData.shafyors && formData.shafyors.length > 0 && (
        <div className="mt-4">
          <h5>Tanlangan shafyorlar</h5>
          <div className="pw-table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ism</th>
                  <th>Telefon</th>
                  <th>Haydovchilik guvohnomasi</th>
                  <th>Harakatlar</th>
                </tr>
              </thead>
              <tbody>
                {formData.shafyors.map((shafyor, index) => (
                  <tr key={shafyor.id || index}>
                    <td>{index + 1}</td>
                    <td>{shafyor.name || 'Noma\'lum'}</td>
                    <td>{shafyor.phone || shafyor.phoneNumber || 'N/A'}</td>
                    <td>{shafyor.prava_raqam || shafyor.driverLicense || 'N/A'}</td>
                    <td>
                      {/*<Button */}
                      {/*  variant="outline-primary" */}
                      {/*  size="sm" */}
                      {/*  className="me-1 pw-btn-action"*/}
                      {/*  onClick={() => handleEditShafyor(shafyor)}*/}
                      {/*>*/}
                      {/*  <FaEdit />*/}
                      {/*</Button>*/}
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        className="pw-btn-action"
                        onClick={() => handleRemoveShafyor(shafyor.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      
      {(!formData.shafyors || formData.shafyors.length === 0) && (
        <div className="pw-alert pw-alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Hech qanday shafyor qo'shilmagan. Iltimos, kamida bitta shafyor qo'shing.
        </div>
      )}

      <div className="pw-alert pw-alert-info mt-4">
        <p className="mb-0">
          <i className="fas fa-info-circle me-2"></i>
          Agar kerakli shafyor ro'yxatda bo'lmasa, "Yangi shafyor qo'shish" tugmasi orqali yangi shafyor qo'shishingiz mumkin.
        </p>
      </div>
      
      {/* Add/Edit Shafyor Modal */}
      {showAddModal && (
        <ShafyorForm
          show={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setEditingShafyor(null);
          }}
          onSave={handleSaveNewShafyor}
          shafyor={editingShafyor}
        />
      )}
    </div>
  );
};

export default Step2Shafyors;
